import type {
  HandlingClass, InvoiceStatus, Order, OrderStatus, Product, StopStatus,
} from '@prody/shared';
import type { Tone } from '~/design-system';

/* ------------------------------------------------------------------ *
 * Domain → visual mapping.
 *
 * This file is the whole reason the tables stay legible: it is the
 * single place where a status becomes a tone and a set of goods
 * becomes a band. If a module ever picks its own colour for
 * "delivered", the system is dead — so nothing else in the app maps
 * state to colour.
 * ------------------------------------------------------------------ */

/** Workflow state → stamp tone. Colour here means "how is it going". */
export const ORDER_TONE: Record<OrderStatus, Tone> = {
  draft: 'neutral',
  placed: 'info',
  sourcing: 'accent',
  picking: 'accent',
  ready: 'info',
  dispatched: 'warn',
  delivered: 'ok',
  invoiced: 'ok',
  cancelled: 'neutral',
};

/** Terminal states get an inked stamp; live ones stay outlined. */
export const isTerminalOrderStatus = (status: OrderStatus): boolean =>
  status === 'delivered' || status === 'invoiced' || status === 'cancelled';

export const INVOICE_TONE: Record<InvoiceStatus, Tone> = {
  draft: 'neutral',
  issued: 'info',
  sent: 'info',
  'part-paid': 'warn',
  paid: 'ok',
  overdue: 'alert',
  disputed: 'alert',
  void: 'neutral',
};

export const STOP_TONE: Record<StopStatus, Tone> = {
  pending: 'neutral',
  'en-route': 'info',
  arrived: 'accent',
  delivered: 'ok',
  partial: 'warn',
  failed: 'alert',
  skipped: 'neutral',
};

/**
 * The band for a whole order.
 *
 * An order that mixes frozen peas and live prawns is, for handling
 * purposes, a live-prawn order: the strictest class on the note is
 * what determines the vehicle, the loading order and how long it can
 * stand on the dock. So the band takes the maximum of a severity
 * ranking rather than the first line's class.
 */
const HANDLING_SEVERITY: Record<HandlingClass, number> = {
  neutral: 0,
  ambient: 1,
  hazmat: 2,
  frozen: 3,
  chilled: 4,
  fresh: 5,
  live: 6,
};

export const strictestHandlingClass = (
  classes: readonly HandlingClass[],
): HandlingClass =>
  classes.reduce<HandlingClass>(
    (worst, c) => (HANDLING_SEVERITY[c] > HANDLING_SEVERITY[worst] ? c : worst),
    'neutral',
  );

export const orderHandlingClass = (
  order: Order,
  productsById: Map<string, Product>,
): HandlingClass =>
  strictestHandlingClass(
    order.lines
      .map((l) => productsById.get(l.productId)?.handlingClass)
      .filter((c): c is HandlingClass => Boolean(c)),
  );

/** Whole-row tint. Reserved for exceptions the eye must not miss. */
export const orderRowTone = (
  order: Order,
): 'warn' | 'alert' | undefined => {
  if (order.status === 'cancelled') return undefined;
  const short = order.lines.filter((l) => l.shortfall);
  if (short.length === 0) return undefined;
  // A shortfall the customer has not been told about is an alert; one
  // that has been substituted is merely a warning.
  return short.some((l) => !l.substitution) ? 'alert' : 'warn';
};

/** A lot's remaining life as a fraction, for the expiry gauge. */
export const remainingShelfLife = (
  bestBefore: string | null,
  shelfLifeDays: number | undefined,
  today: string,
): number | null => {
  if (!bestBefore || !shelfLifeDays) return null;
  const left = Math.floor(
    (Date.parse(`${bestBefore}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
  return Math.max(0, Math.min(1, left / shelfLifeDays));
};

/** Locale-aware product name with a defined fallback chain. */
export const productName = (
  product: Pick<Product, 'name' | 'sku'>,
  locale: string,
  fallbackLocale = 'en',
): string =>
  product.name[locale] ?? product.name[fallbackLocale] ?? Object.values(product.name)[0] ?? product.sku;
